import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import SiteHeader from "@/components/suffa/SiteHeader";
import SiteFooter from "@/components/suffa/SiteFooter";
import SearchPanel from "@/components/suffa/SearchPanel";
import ProfileCard from "@/components/suffa/ProfileCard";
import ProfileModal from "@/components/suffa/ProfileModal";
import InterestDialog from "@/components/suffa/InterestDialog";
import { OrnamentDivider } from "@/components/suffa/Ornaments";
import { DEFAULT_FILTERS } from "@/lib/profiles";
import { useLanguage } from "@/lib/i18n";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { useSeoTitle } from "@/lib/seo";
import "@/pages/FindMatches.css";

const EASE = [0.22, 0.61, 0.36, 1];

const toQuery = (f) => {
  const params = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => {
    if (v) params.set(k, v);
  });
  return params.toString();
};

export default function FindMatches() {
  useSeoTitle("Browse Muslim Profiles — Surat & Worldwide | Suffa Matrimony");
  const { t } = useLanguage();
  const { user } = useAuth();
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [results, setResults] = useState(null); // null = loading
  const [loadError, setLoadError] = useState("");
  const [activeProfile, setActiveProfile] = useState(null);
  const [interestProfile, setInterestProfile] = useState(null);
  const resultsRef = useRef(null);

  const oppositeGender =
    user?.profileGender === "bride" ? "groom" : user?.profileGender === "groom" ? "bride" : "";

  // A logged-in member is matched to the opposite gender by default:
  // a Bride sees eligible Grooms, a Groom sees eligible Brides.
  useEffect(() => {
    if (!oppositeGender) return;
    setDraft((prev) => (prev.lookingFor ? prev : { ...prev, lookingFor: oppositeGender }));
    setApplied((prev) => ({ ...prev, lookingFor: oppositeGender }));
  }, [oppositeGender]);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const qs = toQuery(applied);
        const d = await api.get(`/profiles${qs ? `?${qs}` : ""}`);
        if (on) {
          setResults(d.profiles);
          setLoadError("");
        }
      } catch (e) {
        if (on) {
          setLoadError(e.message);
          setResults([]);
        }
      }
    })();
    return () => {
      on = false;
    };
  }, [applied, user?.id]);

  const update = (field, value) => {
    setDraft((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "ageFrom" && next.ageTo && Number(value) > Number(next.ageTo)) next.ageTo = value;
      if (field === "ageTo" && next.ageFrom && value && Number(value) < Number(next.ageFrom)) {
        next.ageFrom = value;
      }
      return next;
    });
    // Bride/Groom selection refreshes the results immediately (other filters
    // keep their existing Search-button behaviour).
    if (field === "lookingFor") {
      setApplied((prev) => ({ ...prev, lookingFor: value }));
    }
  };

  const applySearch = () => {
    setApplied(draft);
    requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const clearFilters = () => {
    // Keep the member's opposite-gender matching active after clearing.
    const base = { ...DEFAULT_FILTERS, lookingFor: oppositeGender };
    setDraft(base);
    setApplied(base);
  };

  return (
    <div className="suffa-page">
      <SiteHeader />
      <main className="fm-main">
        <motion.section
          className="fm-masthead"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <div className="fm-container fm-masthead-inner">
            <OrnamentDivider />
            <h1 className="fm-title" data-testid="fm-title">
              {t("Find Your Life Partner")}
            </h1>
            <p className="fm-subtitle" data-testid="fm-subtitle">
              {t("Explore Muslim matrimonial profiles and discover a suitable match for marriage.")}
            </p>
          </div>
        </motion.section>

        <motion.div
          className="fm-container"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.12, ease: EASE }}
        >
          <SearchPanel draft={draft} onChange={update} onSearch={applySearch} onClear={clearFilters} />
        </motion.div>

        <section className="fm-results" ref={resultsRef} data-testid="fm-results">
          <div className="fm-container">
            <div className="fm-results-head">
              <h2 className="fm-results-title" data-testid="fm-results-heading">
                Matching Profiles
              </h2>
              <span className="fm-results-count" data-testid="fm-results-count">
                {results?.length ?? "…"}{" "}
                {results?.length === 1 ? t("profile matches your search") : t("profiles match your search")}
              </span>
            </div>

            {results === null ? (
              <p className="fm-empty-title" data-testid="fm-loading">
                {t("Loading profiles…")}
              </p>
            ) : loadError ? (
              <div className="fm-empty" data-testid="fm-error-state">
                <h3 className="fm-empty-title">{t("Something went wrong. Please try again.")}</h3>
                <p>{loadError}</p>
              </div>
            ) : results.length > 0 ? (
              <motion.div
                className="fm-grid"
                key={JSON.stringify(applied) + (user?.id || "guest")}
                initial="hidden"
                animate="show"
                variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}
                data-testid="fm-grid"
              >
                {results.map((p) => (
                  <ProfileCard
                    key={p.id}
                    profile={p}
                    onView={setActiveProfile}
                    onInterested={setInterestProfile}
                  />
                ))}
              </motion.div>
            ) : (
              <div className="fm-empty" data-testid="fm-empty-state">
                <h3 className="fm-empty-title">{t("No matching profiles found")}</h3>
                <p>{t("Try widening the age range or clearing a filter or two.")}</p>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  data-testid="fm-empty-clear"
                  onClick={clearFilters}
                >
                  {t("Clear Filters")}
                </button>
              </div>
            )}
          </div>
        </section>

        <div className="fm-footer-space" />
      </main>

      <SiteFooter />

      {activeProfile && (
        <ProfileModal
          profile={activeProfile}
          onClose={() => setActiveProfile(null)}
          onInterested={setInterestProfile}
        />
      )}
      {interestProfile && (
        <InterestDialog profile={interestProfile} onClose={() => setInterestProfile(null)} />
      )}
    </div>
  );
}
